"use client";

import { useEffect, useState } from "react";

import { useBodyScrollLock } from "@/lib/use-body-scroll-lock";

/**
 * Click-through gate for a content-warning-flagged profile
 * (show.blade.php's `x-data` block). Every visitor sees it — no exemption
 * for logged-in members — and clearing it is remembered per handle in
 * localStorage so it doesn't reappear on the next visit.
 */
export function ContentWarningGate({ handle }: { handle: string }) {
  // Gate shows on first paint (server + client match); an effect then clears it
  // if this visitor already dismissed it for this handle.
  const [revealed, setRevealed] = useState(false);
  useBodyScrollLock(!revealed);

  useEffect(() => {
    let seen = false;
    try {
      seen = localStorage.getItem(`paw:cw:${handle}`) === "1";
    } catch {
      seen = false;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (seen) setRevealed(true);
  }, [handle]);

  if (revealed) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black p-6 text-center">
      <div className="max-w-sm">
        <h1 className="paw-heading text-xl text-white">Content warning</h1>
        <p className="mt-3 text-sm leading-6 text-slate-400">
          This member has flagged their profile as containing sensitive content.
          You must be 18 or older to continue.
        </p>
        <button
          type="button"
          onClick={() => {
            setRevealed(true);
            try {
              localStorage.setItem(`paw:cw:${handle}`, "1");
            } catch {
              /* private mode — gate just won't be remembered */
            }
          }}
          className="paw-btn mt-6 px-6 py-2.5 text-sm"
        >
          I&rsquo;m 18+, continue
        </button>
      </div>
    </div>
  );
}
